import React from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Zap, ArrowRight, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatInr, mergeCreditPlans } from '@/lib/billing/creditPlans';

import { TwinneeLogo } from '@/components/TwinneeLogo';
export function CreditPlansGrid({
  plans: plansProp = null,
  apiPlans = null,
  creditsPerInr = 2,
  billingEnabled = true,
  onBuyPlan,
  payingPlanId = null,
  currentPlanId = '',
  compact = false,
  singleRow = false,
  showCustom = true,
  loading = false,
}) {
  const plans = plansProp || mergeCreditPlans(apiPlans, creditsPerInr);
  const visiblePlans = showCustom ? plans : plans.filter((p) => !p.custom);

  const gridClass = singleRow
    ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4'
    : compact
      ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4'
      : 'grid-cols-1 md:grid-cols-2 xl:grid-cols-4';

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-3" data-testid="credit-plans-grid-loading">
        <Loader2 className="w-8 h-8 animate-spin text-[#2DD4BF]" />
        <span className="text-xs font-mono text-[#9CA3AF]">Loading credit packages...</span>
      </div>
    );
  }

  return (
    <div
      className={`grid gap-3 sm:gap-4 items-stretch ${gridClass} font-jakarta`}
      data-testid="credit-plans-grid"
    >
      {visiblePlans.map((plan) => {
        const isPaying = payingPlanId === plan.id;
        const isPopular = plan.recommended;
        const isCurrent = Boolean(currentPlanId) && currentPlanId === plan.id;

        return (
          <div
            key={plan.id}
            id={`plan-${plan.id}`}
            className={`group relative flex flex-col scroll-mt-24 rounded-[22px] ${
              compact ? 'p-4' : 'p-5 sm:p-6'
            } transition-all duration-300 ${
              isPopular
                ? 'border border-[#2DD4BF]/60 shadow-[0_0_35px_rgba(45,212,191,0.18)] lg:-translate-y-1'
                : 'bg-[#0e131d]/90 border border-white/[0.08] hover:border-white/20 shadow-xl hover:-translate-y-0.5 hover:bg-[#111724]/90'
            }`}
            style={
              isPopular
                ? {
                    background:
                      'linear-gradient(155deg, rgba(58, 38, 106, 0.8) 0%, rgba(18, 30, 48, 0.9) 50%, rgba(12, 45, 52, 0.95) 100%)',
                  }
                : undefined
            }
            data-testid={`credit-plan-${plan.id}`}
          >
            {/* Ambient Background Glow for Popular Card */}
            {isPopular && (
              <>
                <div className="absolute -top-8 -right-8 w-24 h-24 rounded-full bg-[#62FAE3]/20 blur-xl pointer-events-none" />
                <div className="absolute -bottom-8 -left-8 w-24 h-24 rounded-full bg-[#A78BFA]/20 blur-xl pointer-events-none" />
              </>
            )}

            {/* Most Popular Floating Badge */}
            {isPopular && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 rounded-full px-3 py-0.5 text-[9.5px] font-extrabold uppercase tracking-wider font-mono shadow-xl bg-gradient-to-r from-[#D0BCFF] via-[#A78BFA] to-[#62FAE3] text-[#0a0d14] border border-[#2DD4BF]/40 whitespace-nowrap z-10">
                <TwinneeLogo className="w-3 h-3 text-[#0a0d14]" />
                Most Popular
              </div>
            )}

            {/* Plan Header */}
            <div className={`${compact ? 'mb-2.5' : 'mb-4'} relative z-10`}>
              <div className="flex items-center justify-between gap-1.5">
                <h3 className={`${compact ? 'text-lg' : 'text-xl'} font-black font-sora text-white tracking-tight`}>
                  {plan.name}
                </h3>
                {isCurrent ? (
                  <span
                    className="text-[9px] font-mono font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#34d399] text-[#06211b]"
                    data-testid={`current-plan-${plan.id}`}
                  >
                    Current plan
                  </span>
                ) : plan.id === 'studio' ? (
                  <span className="text-[9px] font-mono font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-400/15 border border-amber-400/30 text-amber-300">
                    Pro Scale
                  </span>
                ) : null}
              </div>
              <p className="text-[11.5px] text-gray-400 font-normal mt-0.5 leading-snug">
                {plan.tagline}
              </p>
            </div>

            {/* Pricing Section */}
            {!plan.custom ? (
              <div className={`${compact ? 'mb-3 pb-3 pt-2.5' : 'mb-5 pb-5 pt-3.5'} border-t border-b border-white/[0.08] relative z-10`}>
                <div className="flex items-baseline gap-1.5 flex-wrap">
                  <span className={`${compact ? 'text-2xl sm:text-[26px]' : 'text-3xl'} font-black font-sora text-white tracking-tight`}>
                    {formatInr(plan.offerPriceInr)}
                  </span>
                  {plan.originalPriceInr > plan.offerPriceInr ? (
                    <span className="text-xs sm:text-sm font-semibold text-gray-500 line-through decoration-gray-500/80">
                      {formatInr(plan.originalPriceInr)}
                    </span>
                  ) : null}
                  {plan.discountPercent > 0 ? (
                    <span className="text-[9px] font-bold font-mono tracking-wide px-1.5 py-0.5 rounded-full bg-[#2DD4BF]/15 border border-[#2DD4BF]/30 text-[#62FAE3] uppercase ml-0.5">
                      {plan.discountPercent}% OFF
                    </span>
                  ) : null}
                </div>

                <div className={`${compact ? 'mt-2 p-2' : 'mt-2.5 p-2.5'} flex items-center gap-2 rounded-xl bg-white/[0.04] border border-white/[0.06]`}>
                  <div className="w-5 h-5 rounded-lg bg-[#2DD4BF]/20 flex items-center justify-center shrink-0">
                    <Zap className="w-3 h-3 text-[#62FAE3]" />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-white leading-none">
                      {Number(plan.credits || 0).toLocaleString('en-IN')}{' '}
                      <span className="text-[#62FAE3] font-mono text-[11px]">Credits</span>
                    </p>
                    <p className="text-[9.5px] text-gray-400 mt-0.5">
                      Never expires · Universal usage
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className={`${compact ? 'mb-3 pb-3 pt-2.5' : 'mb-5 pb-5 pt-3.5'} border-t border-b border-white/[0.08] relative z-10`}>
                <p className={`${compact ? 'text-xl' : 'text-2xl'} font-black font-sora text-white`}>
                  Enterprise
                </p>
                <p className="text-[11.5px] text-gray-400 mt-0.5 leading-snug">
                  Custom billing & dedicated processing pipeline
                </p>
                <div className={`${compact ? 'mt-2 p-2' : 'mt-2.5 p-2.5'} rounded-xl bg-white/[0.04] border border-white/[0.06]`}>
                  <p className="text-xs font-semibold text-[#D0BCFF]">
                    Volume Pricing Available
                  </p>
                </div>
              </div>
            )}

            {/* Benefits List */}
            <ul className={`${compact ? 'space-y-1.5 text-xs' : 'space-y-2 text-xs sm:text-[13px]'} flex-1 relative z-10`}>
              {plan.benefits.map((benefit) => (
                <li key={benefit} className="flex items-start gap-2 text-gray-300">
                  <div className="w-3.5 h-3.5 rounded-full bg-[#2DD4BF]/15 border border-[#2DD4BF]/30 flex items-center justify-center shrink-0 mt-0.5">
                    <Check className="w-2 h-2 text-[#62FAE3] stroke-[3]" />
                  </div>
                  <span className="leading-tight text-[11.5px]">{benefit}</span>
                </li>
              ))}
            </ul>

            {/* CTA Button */}
            <div className={`${compact ? 'mt-3.5 pt-1' : 'mt-5 pt-2'} relative z-10`}>
              {plan.custom ? (
                <Button
                  asChild
                  className={`w-full ${compact ? 'py-2' : 'py-2.5'} rounded-full font-bold text-xs tracking-wide bg-white/10 hover:bg-white/20 text-white border border-white/15 hover:border-white/30 transition-all cursor-pointer flex items-center justify-center gap-1.5 shadow-md`}
                >
                  <Link to={plan.ctaPath || '/partners/creative'}>
                    <span>{plan.ctaLabel || 'Talk to Sales'}</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </Button>
              ) : (
                <button
                  type="button"
                  disabled={!billingEnabled || isPaying}
                  onClick={() => onBuyPlan?.(plan)}
                  className={`w-full ${compact ? 'py-2 px-3' : 'py-2.5 px-4'} rounded-full font-bold text-xs tracking-wide transition-all duration-200 cursor-pointer flex items-center justify-center gap-1.5 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed ${
                    isPopular
                      ? 'bg-gradient-to-r from-[#8B5CF6] via-[#6366F1] to-[#2DD4BF] text-white hover:opacity-95 hover:shadow-[0_0_20px_rgba(98,250,227,0.35)] active:scale-[0.98]'
                      : 'bg-white/10 hover:bg-white/20 text-white border border-white/15 hover:border-white/30 active:scale-[0.98]'
                  }`}
                  data-testid={`credit-plan-buy-${plan.id}`}
                >
                  {isPaying ? (
                    <>
                      <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                      <span>Processing...</span>
                    </>
                  ) : (
                    <>
                      <span>{isCurrent ? `Renew ${plan.name}` : `Get ${plan.name}`}</span>
                      <ArrowRight className="w-3.5 h-3.5" />
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default CreditPlansGrid;
